import {computed, Injectable, signal} from '@angular/core';
import type {Option} from '../components/FiltroSelectComponent/filtro-select.component';

@Injectable({ providedIn: 'root' })
export class DemographicsOptionsService {

  private departmentOptions = signal<Option[]>([]);
  private occupationOptions = signal<Option[]>([]);

  readonly catalogsLoaded = computed(() => {
    return this.departmentOptions().length > 0 && this.occupationOptions().length > 0;
  });

  setDepartments(options: Option[]) {
    this.departmentOptions.set(options);
  }

  setOccupations(options: Option[]) {
    this.occupationOptions.set(options);
  }

  /** sexo */
  getGender(): Option[] {
    return [
      { value: 'Femenino', label: 'Femenino' },
      { value: 'Masculino', label: 'Masculino' },
    ];
  }

  /** edad (Edad en años) */
  getAgeRange(): Option[] {
    return [
      { value: '15–19', label: '15 – 19' },
      { value: '20–24', label: '20 – 24' },
      { value: '25–29', label: '25 – 29' },
      { value: '30–34', label: '30 – 34' },
      { value: '35–39', label: '35 – 39' },
      { value: '40–44', label: '40 – 44' },
      { value: '45-49', label: '45 - 49' },
      { value: '50-54', label: '50 - 54' },
      { value: '55-59', label: '55 - 59' },
      { value: '60-64', label: '60 - 64' },
      { value: '65-69', label: '65 - 69' },
      { value: '70 o más', label: '70 o más' },
    ];
  }

  /** estadoCivil */
  getMaritalStatus(): Option[] {
    return [
      { value: 'Casado', label: 'Casado' },
      { value: 'Soltero', label: 'Soltero' },
      { value: 'Unión libre', label: 'Unión libre' },
      { value: 'Divorciado', label: 'Divorciado' },
      { value: 'Viudo', label: 'Viudo' },
    ];
  }

  /** ocupacionProfesionPuesto (se llena dinámico) */
  getOccupation(): Option[] {
    return this.occupationOptions();
  }

  /** departamentoSeccionArea (se llena dinámico) */
  getDepartment(): Option[] {
    return this.departmentOptions();
  }

  /** nivelEstudios */
  getEducationLevel(): Option[] {
    return [
      { value: 'Sin formación', label: 'Sin formación' },
      { value: 'Primaria', label: 'Primaria' },
      { value: 'Secundaria', label: 'Secundaria' },
      { value: 'Preparatoria o Bachillerato', label: 'Preparatoria o Bachillerato' },
      { value: 'Técnico Superior', label: 'Técnico Superior' },
      { value: 'Licenciatura', label: 'Licenciatura' },
      { value: 'Maestría', label: 'Maestría' },
      { value: 'Doctorado', label: 'Doctorado' },
    ];
  }

  /** estadoNivelEstudios */
  getEducationStatus(): Option[] {
    return [
      { value: 'Terminada', label: 'Terminada' },
      { value: 'Incompleta', label: 'Incompleta' },
    ];
  }

  /** tipoPuesto */
  getPosition(): Option[] {
    return [
      { value: 'Operativo', label: 'Operativo' },
      { value: 'Profesional o técnico', label: 'Profesional o técnico' },
      { value: 'Supervisor', label: 'Supervisor' },
      { value: 'Gerente', label: 'Gerente' },
    ];
  }

  /** tipoContratacion */
  getContractType(): Option[] {
    return [
      { value: 'Por obra o proyecto', label: 'Por obra o proyecto' },
      { value: 'Por tiempo determinado (temporal)', label: 'Por tiempo determinado (temporal)' },
      { value: 'Tiempo indeterminado', label: 'Tiempo indeterminado' },
      { value: 'Honorarios', label: 'Honorarios' },
    ];
  }

  /** tipoPersonal */
  getEmployeeType(): Option[] {
    return [
      { value: 'Sindicalizado', label: 'Sindicalizado' },
      { value: 'Confianza', label: 'Confianza' },
      { value: 'Ninguno', label: 'Ninguno' },
    ];
  }

  /** tipoJornada */
  getWorkSchedule(): Option[] {
    return [
      { value: 'Fijo nocturno (entre las 20:00 y 6:00 hrs)', label: 'Fijo nocturno (entre las 20:00 y 6:00 hrs)' },
      { value: 'Fijo diurno (entre las 6:00 y 20:00 hrs)', label: 'Fijo diurno (entre las 6:00 y 20:00 hrs)' },
      { value: 'Fijo mixto (combinación de nocturno y diurno)', label: 'Fijo mixto (combinación de nocturno y diurno)' },
    ];
  }

  /** rotacionTurno */
  getHasShiftRotation(): Option[] {
    return [
      { value: 'true', label: 'Sí' },
      { value: 'false', label: 'No' },
    ];
  }

  /** antiguedad (Tiempo en puesto actual) */
  getSeniority(): Option[] {
    return [
      { value: 'Menos de 6 meses', label: 'Menos de 6 meses' },
      { value: 'Entre 6 meses y 1 año', label: 'Entre 6 meses y 1 año' },
      { value: 'Entre 1 a 4 años', label: 'Entre 1 a 4 años' },
      { value: 'Entre 5 a 9 años', label: 'Entre 5 a 9 años' },
      { value: 'Entre 10 a 14 años', label: 'Entre 10 a 14 años' },
      { value: 'Entre 15 a 19 años', label: 'Entre 15 a 19 años' },
      { value: 'Entre 20 a 24 años', label: 'Entre 20 a 24 años' },
      { value: '25 años o más', label: '25 años o más' },
    ];
  }

  /** experienciaLaboral (Tiempo experiencia laboral) */
  getTotalWorkExperience(): Option[] {
    return [
      { value: 'Menos de 6 meses', label: 'Menos de 6 meses' },
      { value: 'Entre 6 meses y 1 año', label: 'Entre 6 meses y 1 año' },
      { value: 'Entre 1 a 4 años', label: 'Entre 1 a 4 años' },
      { value: 'Entre 5 a 9 años', label: 'Entre 5 a 9 años' },
      { value: 'Entre 10 a 14 años', label: 'Entre 10 a 14 años' },
      { value: 'Entre 15 a 19 años', label: 'Entre 15 a 19 años' },
      { value: 'Entre 20 a 24 años', label: 'Entre 20 a 24 años' },
      { value: '25 años o más', label: '25 años o más' },
    ];
  }
}
